import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { Input } from '@/components/atoms/Input';
import { Select } from '@/components/atoms/Select';
import { Badge } from '@/components/atoms/Badge';
import { Card } from '@/components/molecules/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/molecules/Dialog';
import { DataTable } from '@/components/organisms/DataTable';
import type { DataTableColumn } from '@/components/organisms/DataTable';
import { useStories, useDeleteStory } from '@/hooks/useBackofficeData';
import { useToast } from '@/components/atoms/Toast';
import type { BackofficeStory } from '@/types/backoffice.types';
import { ROUTES } from '@/constants/routes';
import styles from './StoryListPage.module.scss';

export function StoryListPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [storyToDelete, setStoryToDelete] = useState<BackofficeStory | null>(null);

  const { stories, isLoading, refetch } = useStories();
  const { deleteStory, isDeleting } = useDeleteStory();
  const { toast } = useToast();

  const filteredStories = useMemo(
    () =>
      stories.filter((story) => {
        const matchesSearch =
          story.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          story.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || story.status === statusFilter;
        const matchesLevel = levelFilter === 'all' || story.level === levelFilter;
        return matchesSearch && matchesStatus && matchesLevel;
      }),
    [stories, searchQuery, statusFilter, levelFilter]
  );

  const handleConfirmDelete = async () => {
    if (!storyToDelete) return;
    const title = storyToDelete.title;
    try {
      await deleteStory(storyToDelete.id);
      setStoryToDelete(null);
      await refetch();
      toast({
        variant: 'success',
        title: 'Historia eliminada',
        description: `"${title}" y todos sus datos asociados se han eliminado.`,
      });
    } catch {
      toast({
        title: 'Error al eliminar',
        description: 'No se ha podido eliminar la historia. Inténtalo de nuevo.',
      });
    }
  };

  const columns: DataTableColumn<BackofficeStory>[] = [
    {
      key: 'title',
      header: 'Título',
      accessor: (row) => row.title,
      sortable: true,
      width: '30%',
    },
    {
      key: 'level',
      header: 'Nivel',
      accessor: (row) => <Badge>{row.level}</Badge>,
      sortable: true,
    },
    {
      key: 'category',
      header: 'Categoría',
      accessor: (row) => row.category,
      sortable: true,
    },
    {
      key: 'status',
      header: 'Estado',
      accessor: (row) => {
        const statusLabels: Record<string, string> = {
          published: 'Publicado',
          draft: 'Borrador',
          archived: 'Archivado',
        };
        const variant =
          row.status === 'published'
            ? 'success'
            : row.status === 'draft'
            ? 'default'
            : 'default';
        return <Badge variant={variant}>{statusLabels[row.status] ?? row.status}</Badge>;
      },
      sortable: true,
    },
    {
      key: 'completions',
      header: 'Completados',
      accessor: (row) => row.completionsCount.toString(),
      sortable: true,
      align: 'right',
    },
    {
      key: 'avgScore',
      header: 'Punt. media',
      accessor: (row) => `${row.averageScore}%`,
      sortable: true,
      align: 'right',
    },
    {
      key: 'actions',
      header: '',
      width: '64px',
      align: 'right',
      accessor: (row) => (
        // Pre-existente: el click stopPropagation evita disparar onRowClick; no se
        // necesita teclado porque ya hay botones interactivos dentro.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <span
          onClick={(e) => e.stopPropagation()}
          className={styles.storyList__rowActions}
        >
          <IconButton
            icon={Trash2}
            variant="destructive"
            size="sm"
            aria-label={`Eliminar historia ${row.title}`}
            onClick={() => setStoryToDelete(row)}
          />
        </span>
      ),
    },
  ];

  const handleRowClick = (story: BackofficeStory) => {
    navigate(`${ROUTES.BACKOFFICE.STORIES}/${story.id}/edit`);
  };

  return (
    <div className={styles.storyList}>
      <div className={styles.storyList__header}>
        <div>
          <h1>Historias</h1>
          <p className={styles.storyList__subtitle}>Gestiona todas las historias de aprendizaje</p>
        </div>
        <div className={styles.storyList__actions}>
          <Button variant="ghost" leftIcon={<Sparkles size={16} />} onClick={() => navigate(ROUTES.BACKOFFICE.STORY_GENERATE)}>
            Generar con IA
          </Button>
          <Button leftIcon={<Plus />} onClick={() => navigate(ROUTES.BACKOFFICE.STORY_TYPE_SELECT)}>
            Crear historia
          </Button>
        </div>
      </div>

      <Card className={styles.storyList__filters}>
        <div className={styles.storyList__filtersGrid}>
          <Input
            type="search"
            placeholder="Buscar historias..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Select
            placeholder="Todos los estados"
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={[
              { label: 'Todos los estados', value: 'all' },
              { label: 'Publicado', value: 'published' },
              { label: 'Borrador', value: 'draft' },
              { label: 'Archivado', value: 'archived' },
            ]}
          />
          <Select
            placeholder="Todos los niveles"
            value={levelFilter}
            onValueChange={setLevelFilter}
            options={[
              { label: 'Todos los niveles', value: 'all' },
              { label: 'A1', value: 'A1' },
              { label: 'A2', value: 'A2' },
              { label: 'B1', value: 'B1' },
              { label: 'B2', value: 'B2' },
              { label: 'C1', value: 'C1' },
              { label: 'C2', value: 'C2' },
            ]}
          />
        </div>
      </Card>

      <DataTable
        columns={columns}
        data={filteredStories}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        emptyMessage="No se encontraron historias"
      />

      <Dialog
        open={storyToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setStoryToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar historia</DialogTitle>
            <DialogDescription>
              ¿Seguro que quieres eliminar <strong>{storyToDelete?.title}</strong>?
              <br />
              Se eliminarán también todos los datos asociados: resultados, asignaciones,
              progreso, errores, palabras aprendidas e intentos de pronunciación de los usuarios.
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStoryToDelete(null)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Eliminando…' : 'Eliminar historia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StoryListPage;
